/**
 * The slide typography unit system — the single place the pt↔px relationship
 * lives.
 *
 * ## The unit: slide points (pt)
 *
 * All slide-surface text sizes are expressed in **slide points**, matching
 * PowerPoint/Google Slides font sizes: a slide element's `fontSize` field
 * (see `slides/schema/deck.ts`), the editor ribbon's font-size control, and
 * demo-internal typography (via {@link slidePt}) all speak this one unit —
 * "size 24" always means the same visual size, no matter who writes it where.
 *
 * The slide canvas is a fixed `CANVAS_WIDTH`×`CANVAS_HEIGHT` (1920×1080)
 * coordinate space representing a 13.33in × 7.5in slide — i.e. 144 dpi.
 * A point is 1/72 in, so on this canvas **1 pt = exactly 2 canvas px**
 * ({@link SLIDE_PT_TO_PX}). Every view scales the whole canvas to fit the
 * screen, so canvas px (and therefore pt) are resolution-independent.
 *
 * ## Rules of thumb (for humans and agents)
 *
 * - **Text rendered on the slide surface** — slide elements, and any text
 *   inside an embedded demo (`demos/`) — sizes in pt: store pt in `fontSize`
 *   fields, write `font-size: ${slidePt(24)}` in demo CSS. Never hardcode
 *   `px` for demo text: inside a slide, css px are canvas px, worth *half* a
 *   point, which is how "size 24" text ends up looking like size 12.
 * - **App/editor chrome** (ribbon, panes, dialogs — anything not on the slide
 *   canvas) — ordinary CSS `px`; pt does not apply.
 * - **SVG `<text>` sized in viewBox units** (common in demo visualizations) —
 *   exempt; those scale with their own viewBox, not the canvas.
 * - **Relative units** (`em`, `cqw`, `%`) — fine where they derive from a
 *   pt-based size (e.g. `clamp(${slidePt(7)}, 2cqw, ${slidePt(12)})`); avoid
 *   `rem` on the slide surface, since it binds to the browser's root font
 *   size instead of the slide.
 */

/**
 * Canvas px per slide point on the fixed 1920×1080 canvas (144 dpi ÷ 72
 * pt/in = 2). `slides/shared/slideTheme.ts`'s `FONT_SIZE_RENDER_SCALE`
 * re-exports this value for the slide renderer; demos should not need it
 * directly — use {@link slidePt}.
 */
export const SLIDE_PT_TO_PX = 2;

/**
 * A CSS length for `pt` slide points (see the module doc comment), for any
 * text drawn on the slide surface — e.g. `font-size: ${slidePt(24)}` renders
 * exactly like a slide text element with `fontSize: 24`.
 *
 * @param pt - Size in slide points. Fractional values are fine (e.g. `7.5`).
 * @return The equivalent CSS px length, e.g. `slidePt(24)` → `'48px'`.
 * @throws Never.
 */
export function slidePt(pt: number): string {
  return `${String(pt * SLIDE_PT_TO_PX)}px`;
}
